<template>
  <div ref="frame" class="finder" :class="[state, { 'has-shot': hasShot }]">
    <slot></slot>

    <!-- The caption is the whole of the framing feedback, so it is a live region: a
         screen reader announces "move a little closer" as it changes. -->
    <div
      v-if="caption"
      class="cap"
      :class="'cap-' + guide"
      role="status"
      aria-live="polite"
    >
      {{ caption }}
    </div>

    <div v-if="state !== 'captured'" ref="oval" class="oval" :class="'guide-' + guide"></div>
    <div class="brackets"><i></i><i></i><i></i><i></i></div>

    <slot name="overlay"></slot>

    <div v-if="flashing" class="flash go"></div>
  </div>
</template>

<script>
/**
 * The camera frame: rounded viewport, framing oval, corner brackets and the status
 * caption. Shared by the photo step and the video step so the two look identical.
 *
 * Purely presentational — it holds no stream. The media element and any step-specific
 * overlay (record pill, playback controls) come in through the slots.
 */
export default {
  name: "ViewFinder",

  props: {
    /**
     * idle     — no stream yet: dimmed, no live indicator.
     * live     — showing the camera.
     * captured — showing a still or a recording, framing chrome removed.
     */
    state: {
      type: String,
      default: "idle",
      validator: (value) => ["idle", "live", "captured"].indexOf(value) !== -1,
    },
    caption: { type: String, default: "" },
    /** A still image is filling the frame (drives the `.shot` visibility rule). */
    hasShot: { type: Boolean, default: false },
    /** Momentary white flash on shutter. */
    flashing: { type: Boolean, default: false },
    /**
     * Face-framing feedback, drawn on the oval itself.
     *
     * off       — no framing check is running (the video step); the oval stays white.
     * searching — looking for a face.
     * adjust    — a face is there but badly framed.
     * ok        — framed; the shutter is live.
     */
    guide: {
      type: String,
      default: "off",
      validator: (value) => ["off", "searching", "adjust", "ok"].indexOf(value) !== -1,
    },
  },

  methods: {
    /**
     * Where the guide oval sits, normalised to the frame (0–1), plus the frame's aspect
     * ratio. Measured rather than derived from the CSS so the two cannot drift apart, and
     * so a change to the oval's size stays a pure style change.
     *
     * @returns {{target:{x,y,width,height}, aspect:number}|null}
     */
    geometry() {
      const frame = this.$refs.frame;
      const oval = this.$refs.oval;
      if (!frame || !oval) return null;

      const frameBox = frame.getBoundingClientRect();
      const ovalBox = oval.getBoundingClientRect();
      if (!frameBox.width || !frameBox.height) return null;

      return {
        target: {
          x: (ovalBox.left - frameBox.left) / frameBox.width,
          y: (ovalBox.top - frameBox.top) / frameBox.height,
          width: ovalBox.width / frameBox.width,
          height: ovalBox.height / frameBox.height,
        },
        aspect: frameBox.width / frameBox.height,
      };
    },
  },
};
</script>

<style scoped>
.finder {
  position: relative;
  flex: 1;
  min-height: 280px;
  border-radius: 22px;
  overflow: hidden;
  margin-top: 8px;
  background: radial-gradient(120% 90% at 50% 20%, #243042 0%, #12151d 55%, #0a0c11 100%);
  border: 1px solid var(--border);
}

.finder.idle {
  filter: saturate(0.6) brightness(0.7);
}

.finder.captured {
  background: radial-gradient(120% 90% at 50% 30%, #2b3a34 0%, #182420 60%, #0c0f0d 100%);
}

.cap {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  background: rgba(8, 10, 16, 0.62);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  padding: 7px 14px;
  border-radius: 999px;
  backdrop-filter: blur(6px);
  white-space: nowrap;
}

.finder.captured .cap {
  background: var(--success);
  color: #08150e;
  font-weight: 700;
}

.oval {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -46%);
  width: 60%;
  aspect-ratio: 3/4;
  border: 3px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  box-shadow: 0 0 0 1000px rgba(6, 8, 13, 0.28);
  z-index: 2;
  transition: border-color 0.22s ease, box-shadow 0.22s ease;
}

/* Framing feedback lives on the oval the user is already looking at, so the answer to
   "am I in the right place" is in the same place as the question. Colours are literal
   rather than themed: they sit on the camera image, not on the page background. */
.oval.guide-adjust {
  border-color: #f5b73d;
  box-shadow: 0 0 0 1000px rgba(6, 8, 13, 0.28), 0 0 18px rgba(245, 183, 61, 0.45);
}

.oval.guide-ok {
  border-color: #37d39f;
  box-shadow: 0 0 0 1000px rgba(6, 8, 13, 0.28), 0 0 22px rgba(55, 211, 159, 0.55);
}

.cap-adjust {
  background: rgba(245, 183, 61, 0.92);
  color: #241a02;
  font-weight: 700;
}

.cap-ok {
  background: #37d39f;
  color: #08150e;
  font-weight: 700;
}

.brackets i {
  position: absolute;
  width: 24px;
  height: 24px;
  border: 3px solid rgba(255, 255, 255, 0.5);
  z-index: 2;
}

.brackets i:nth-child(1) {
  top: 16px;
  right: 16px;
  border-left: 0;
  border-bottom: 0;
  border-radius: 0 8px 0 0;
}
.brackets i:nth-child(2) {
  top: 16px;
  left: 16px;
  border-right: 0;
  border-bottom: 0;
  border-radius: 8px 0 0 0;
}
.brackets i:nth-child(3) {
  bottom: 16px;
  right: 16px;
  border-left: 0;
  border-top: 0;
  border-radius: 0 0 8px 0;
}
.brackets i:nth-child(4) {
  bottom: 16px;
  left: 16px;
  border-right: 0;
  border-top: 0;
  border-radius: 0 0 0 8px;
}

.flash {
  position: absolute;
  inset: 0;
  background: #fff;
  opacity: 0;
  z-index: 5;
  pointer-events: none;
}

@media (prefers-reduced-motion: no-preference) {
  .flash.go {
    animation: flash 0.5s ease-out;
  }
  @keyframes flash {
    0% {
      opacity: 0;
    }
    12% {
      opacity: 0.9;
    }
    100% {
      opacity: 0;
    }
  }
}
</style>

<style>
/* Media and overlays arrive through the slots, so they are owned by the parent component
   and cannot be reached by a scoped rule here. Namespaced under `.finder`. */
.finder video,
.finder .shot {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}

/* The preview is mirrored so it behaves like a mirror; `captureFrame()` applies the same
   flip to the canvas, so a captured still is already correct and must NOT be flipped again. */
.finder video {
  transform: scaleX(-1);
  display: none;
}

.finder.live video:not(.playback) {
  display: block;
}

.finder .shot {
  display: none;
  z-index: 1;
}

.finder.has-shot .shot {
  display: block;
}

.finder .playback {
  transform: scaleX(-1);
  display: none;
  z-index: 1;
  background: #0c0f0d;
}

.finder.has-video .playback {
  display: block;
}

.finder.has-video .shot {
  display: none;
}

/* status pill, bottom-centre */
.finder .live {
  position: absolute;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 7px;
  color: #fff;
  font-size: 12.5px;
  font-weight: 600;
  background: rgba(8, 10, 16, 0.62);
  padding: 6px 13px;
  border-radius: 999px;
  backdrop-filter: blur(6px);
  white-space: nowrap;
}

.finder .live b {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  min-width: 1.15em;
  display: inline-block;
  text-align: center;
}

.finder .live .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #37d39f;
}

.finder .live.rec .dot {
  background: #ff5a5a;
}

@media (prefers-reduced-motion: no-preference) {
  .finder .live .dot {
    animation: blink 1.2s steps(1) infinite;
  }
  @keyframes blink {
    50% {
      opacity: 0.3;
    }
  }
}
</style>
